import { type DungeonIntent, idx } from '@xox/shared'
import { ArrowDownToLine, ArrowLeft, Hourglass, LogOut } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { DPad } from '../components/DPad'
import { Button, Centered, IconButton, IconLink } from '../components/ui'
import { DungeonBoard } from '../dungeon/DungeonBoard'
import { Hud } from '../dungeon/Hud'
import { InventoryBar } from '../dungeon/InventoryBar'
import { PerkSheet } from '../dungeon/PerkSheet'
import { RunEndOverlay } from '../dungeon/RunEndOverlay'
import { useDungeonInput } from '../dungeon/useDungeonInput'
import { api } from '../lib/api'
import { useDungeon } from '../store/dungeon'

export function DungeonRun() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const run = useDungeon()
  const [peek, setPeek] = useState(false)
  const [leaving, setLeaving] = useState(false)

  useEffect(() => {
    void useDungeon.getState().open(id)
    return () => useDungeon.getState().close()
  }, [id])

  const view = run.view
  const active = !!view && view.status === 'active'
  const busy = !!run.inFlight
  const enqueue = useCallback((i: DungeonIntent) => useDungeon.getState().enqueue(i), [])
  const input = useDungeonInput(
    active && view.pendingPerks.length === 0,
    view?.hero.pos ?? { r: 0, c: 0 },
    enqueue,
  )

  // Hand the board the events of the last answer exactly once.
  const fx = run.fx
  useEffect(() => {
    if (fx.length > 0) {
      const t = setTimeout(() => useDungeon.getState().takeFx(), 0)
      return () => clearTimeout(t)
    }
  }, [fx])

  if (run.error && !view) {
    return (
      <Centered>
        <p className="text-rose-600">{run.error}</p>
      </Centered>
    )
  }
  if (!view) return <Centered>Entering the dungeon…</Centered>

  const onStairs = view.tiles[idx(view.hero.pos)] === '>'
  const abandon = async () => {
    if (!leaving) {
      setLeaving(true)
      return
    }
    const res = await api.abandonDungeonRun(view.id)
    useDungeon.setState({ view: res.view, end: res.end ?? null, queue: [], inFlight: null })
    setLeaving(false)
  }
  const practice = async () => {
    const { run: r } = await api.startDungeonRun({ kind: 'practice', cls: view.hero.cls })
    navigate(`/dungeon/run/${r.id}`)
  }

  return (
    <div className="flex flex-1 flex-col items-center gap-3">
      <div className="flex w-full max-w-[400px] items-center justify-between gap-2 text-sm">
        <IconLink to="/dungeon" label="Back to the dungeon">
          <ArrowLeft className="size-4" aria-hidden />
        </IconLink>
        <span className="min-w-0 truncate font-semibold">
          {view.kind === 'daily' ? `Daily · ${view.day}` : 'Practice run'}
        </span>
        {active ? (
          leaving ? (
            <span className="flex items-center gap-1">
              <Button variant="danger" className="min-h-8 px-2 py-1 text-xs" onClick={abandon}>
                End run
              </Button>
              <Button
                variant="ghost"
                className="min-h-8 px-2 py-1 text-xs"
                onClick={() => setLeaving(false)}
              >
                Keep going
              </Button>
            </span>
          ) : (
            <IconButton label="End this run" onClick={abandon}>
              <LogOut className="size-4" aria-hidden />
            </IconButton>
          )
        ) : (
          <span className="w-10" />
        )}
      </div>

      <Hud view={view} />

      <div className="relative">
        <DungeonBoard view={view} fx={fx} {...(active ? input : {})} />
        {!active && !peek && (
          <RunEndOverlay
            view={view}
            end={run.end}
            onPeek={() => setPeek(true)}
            onPractice={practice}
          />
        )}
        {run.conflict && (
          <div className="pointer-events-none absolute inset-x-0 top-3 z-10 flex justify-center">
            <span className="rounded-full bg-slate-900/80 px-3 py-1 text-xs font-bold text-white">
              Synced with your other tab
            </span>
          </div>
        )}
      </div>

      <p
        className="min-h-5 max-w-[400px] text-center text-sm text-slate-600 dark:text-slate-300"
        aria-live="polite"
      >
        {view.log[view.log.length - 1] ?? ''}
      </p>

      <InventoryBar
        items={view.hero.inventory}
        onUse={(slot) => enqueue({ type: 'use', slot })}
        disabled={!active || view.pendingPerks.length > 0}
      />

      {active && (
        <div className="flex items-center gap-4 sm:hidden">
          <DPad
            verb="Move"
            onMove={(dir) => enqueue({ type: 'move', dir })}
            disabled={busy && run.queue.length >= 3}
          />
          <div className="flex flex-col gap-2">
            <Button variant="secondary" onClick={() => enqueue({ type: 'wait' })}>
              <Hourglass className="size-4" aria-hidden /> Wait
            </Button>
            <Button
              variant="arcade"
              tone="go"
              className="min-h-11 px-4 text-sm"
              disabled={!onStairs}
              onClick={() => enqueue({ type: 'descend' })}
            >
              <ArrowDownToLine className="size-4" aria-hidden /> Descend
            </Button>
          </div>
        </div>
      )}
      <p className="hidden text-xs text-slate-500 sm:block">
        Arrows or WASD move · Space waits · Enter descends on the stairs · 1–5 use a pocket · tap a
        neighbouring cell on touch
      </p>

      {run.error && view && (
        <div className="flex items-center gap-2 rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-200 dark:ring-rose-900">
          {run.error}
          <Button variant="secondary" className="min-h-8 px-2 py-1 text-xs" onClick={run.retry}>
            Retry
          </Button>
        </div>
      )}

      {/* A finished run can carry a perk offer from its last blow; there is nothing left to pick for. */}
      <PerkSheet
        perks={active ? view.pendingPerks : []}
        onPick={(index) => enqueue({ type: 'perk', index })}
      />
    </div>
  )
}
